/**
 * Workflow Criteria Governance helpers
 *
 * Provides the runtime enforcement layer for WorkflowConfiguration.criteria_governance.
 *
 * Two problems addressed:
 *   1. Prevent mid-cycle criteria changes — when a workflow owns the criteria set
 *      ('workflow_defined' mode), locked attributes cannot be removed from any cycle
 *      that uses the workflow.
 *
 *   2. Prevent cross-cycle range drift — when a locked attribute carries a lockedRange,
 *      every parallel cycle using the same workflow must use that exact range. A
 *      proposeRange call with a diverging range is rejected before it reaches the DB.
 *
 * Architecture note:
 *   The Workflow is "always live" (canon: "Workflow is always live. Templates are applied
 *   once."). CycleTemplate seeds values at creation; WorkflowCriteriaGovernance enforces
 *   them for the duration of the cycle. The template provides the default; the workflow
 *   provides the ongoing constraint.
 */

import { getSafeSupabaseClient } from './supabaseSync';
import { fromDbFormat } from './supabaseSync';
import { getEffectiveWorkflowConfig } from './workflowConfig';
import type {
  WorkflowCriteriaGovernance,
  WorkflowLockedAttribute,
  WorkflowRangePreset,
  Workflow,
} from '../types/prioritization/workflow';

// ============================================================================
// Resolution
// ============================================================================

/**
 * Resolve the effective criteria governance for a cycle.
 *
 * Lookup order mirrors resolveEffectiveWorkflowConfig:
 *   cycle.workflow_id → workspace.default_workflow_id → canonical default
 *
 * Returns null only when Supabase is unavailable (fail-open: don't block mutations
 * on infrastructure failures).
 */
export async function resolveWorkflowCriteriaGovernance(
  cycleId: string,
): Promise<WorkflowCriteriaGovernance | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  // 1. Get cycle → workflow_id + workspace_id
  const { data: cycleRow } = await supabase
    .from('degap_cycles')
    .select('workflow_id, workspace_id')
    .eq('id', cycleId)
    .single();

  if (!cycleRow) return null;

  const cycle = fromDbFormat<{ workflowId: string | null; workspaceId: string | null }>(
    cycleRow as Record<string, unknown>,
  );

  let workflowId: string | null = cycle.workflowId ?? null;

  // 2. Fall back to workspace default workflow
  if (!workflowId && cycle.workspaceId) {
    const { data: wsRow } = await supabase
      .from('workspaces')
      .select('default_workflow_id')
      .eq('id', cycle.workspaceId)
      .single();
    const ws = wsRow
      ? fromDbFormat<{ defaultWorkflowId: string | null }>(wsRow as Record<string, unknown>)
      : null;
    workflowId = ws?.defaultWorkflowId ?? null;
  }

  if (!workflowId) return null;

  // 3. Load workflow
  const { data: wfRow } = await supabase
    .from('workflows')
    .select('*')
    .eq('id', workflowId)
    .single();

  if (!wfRow) return null;

  const workflow = fromDbFormat<Workflow>(wfRow as Record<string, unknown>);
  const effective = getEffectiveWorkflowConfig(workflow);
  return effective.criteriaGovernance;
}

// ============================================================================
// Attribute-level checks
// ============================================================================

/**
 * Whether the workflow locks this attribute (i.e. it cannot be removed from any
 * cycle using this workflow).
 */
export function isAttributeWorkflowLocked(
  governance: WorkflowCriteriaGovernance,
  attributeKey: string,
): boolean {
  if (governance.mode !== 'workflow_defined') return false;
  return (governance.lockedAttributes ?? []).some(
    (a) => a.attributeKey === attributeKey && a.required,
  );
}

/**
 * Whether this attribute has a canonical range locked by the workflow.
 * When true, cycles cannot propose a different range.
 */
export function isRangeWorkflowLocked(
  governance: WorkflowCriteriaGovernance,
  attributeKey: string,
): boolean {
  if (governance.mode !== 'workflow_defined') return false;
  const entry = (governance.lockedAttributes ?? []).find(
    (a) => a.attributeKey === attributeKey,
  );
  return !!entry?.lockedRange;
}

/**
 * Return the canonical locked range for an attribute, or null if not locked.
 */
export function getLockedRange(
  governance: WorkflowCriteriaGovernance,
  attributeKey: string,
): WorkflowRangePreset | null {
  if (governance.mode !== 'workflow_defined') return null;
  return (
    (governance.lockedAttributes ?? []).find((a) => a.attributeKey === attributeKey)
      ?.lockedRange ?? null
  );
}

// ============================================================================
// Range comparison
// ============================================================================

/**
 * Normalise a range value for equality comparison, stripping undefined entries
 * and sorting array values so the comparison is order-independent.
 */
function normaliseRange(r: WorkflowRangePreset | Record<string, unknown>): string {
  const obj: Record<string, unknown> = {
    type: r.type,
  };
  const withOptions = r as { options?: unknown[] };
  if (Array.isArray(withOptions.options) && withOptions.options.length > 0) {
    obj['options'] = [...withOptions.options].sort();
  }
  const withNum = r as { min?: number; max?: number; step?: number };
  if (withNum.min != null) obj['min'] = withNum.min;
  if (withNum.max != null) obj['max'] = withNum.max;
  if (withNum.step != null) obj['step'] = withNum.step;
  return JSON.stringify(obj, Object.keys(obj).sort());
}

/**
 * Returns true when `proposed` is functionally equivalent to `locked`.
 * Handles the tshirt/select alias (both serialise to 'select').
 */
export function rangeMatchesLocked(
  proposed: WorkflowRangePreset | Record<string, unknown>,
  locked: WorkflowRangePreset,
): boolean {
  // Normalise tshirt → select alias
  const normType = (t: string) => (t === 'tshirt' ? 'select' : t);
  const p = { ...proposed, type: normType(String(proposed.type ?? '')) };
  const l = { ...locked, type: normType(locked.type) };
  return normaliseRange(p) === normaliseRange(l);
}

// ============================================================================
// Composite enforcement result
// ============================================================================

export interface CriteriaGovernanceViolation {
  code:
    | 'CRITERIA_WORKFLOW_LOCKED'   // Attempted to remove a required locked attribute
    | 'RANGE_WORKFLOW_LOCKED';     // Proposed range diverges from workflow canonical range
  attributeKey: string;
  message: string;
}

/**
 * Check whether removing an attribute from a cycle violates workflow governance.
 * Returns a violation record if blocked, null if allowed.
 */
export function checkCanRemoveAttribute(
  governance: WorkflowCriteriaGovernance,
  attributeKey: string,
): CriteriaGovernanceViolation | null {
  if (!isAttributeWorkflowLocked(governance, attributeKey)) return null;
  return {
    code: 'CRITERIA_WORKFLOW_LOCKED',
    attributeKey,
    message: `Criterion "${attributeKey}" is controlled by the workflow and cannot be removed from this cycle.`,
  };
}

/**
 * Check whether a proposed range for an attribute violates workflow governance.
 * Returns a violation record if blocked, null if allowed.
 */
export function checkCanProposeRange(
  governance: WorkflowCriteriaGovernance,
  attributeKey: string,
  proposedRange: WorkflowRangePreset | Record<string, unknown>,
): CriteriaGovernanceViolation | null {
  const locked = getLockedRange(governance, attributeKey);
  if (!locked) return null;
  if (rangeMatchesLocked(proposedRange, locked)) return null;
  return {
    code: 'RANGE_WORKFLOW_LOCKED',
    attributeKey,
    message:
      `The value range for "${attributeKey}" is defined at the workflow level and cannot be changed per-cycle. ` +
      `All cycles using this workflow must use the same range.`,
  };
}

/**
 * Get the locked attribute entry for a given key, if any.
 */
export function getLockedAttributeEntry(
  governance: WorkflowCriteriaGovernance,
  attributeKey: string,
): WorkflowLockedAttribute | null {
  if (governance.mode !== 'workflow_defined') return null;
  return (governance.lockedAttributes ?? []).find((a) => a.attributeKey === attributeKey) ?? null;
}
